import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import { SITE_NAME, SITE_URL, usePageSeo } from "@/hooks/usePageSeo";
import { categories, works } from "@/mocks/works";
import { formatNumber } from "@/utils/format";
import type { Work } from "@/types/work";

const sortOptions = [
  { key: "latest", label: "最新投稿" },
  { key: "hot", label: "最多浏览" },
  { key: "likes", label: "最多点赞" },
];

export default function Works() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const category = params.get("category") ?? "all";
  const sort = params.get("sort") ?? "latest";
  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const activeCategory = categories.find((c) => c.id === category);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || value === "latest") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next);
  };

  const result: Work[] = useMemo(() => {
    const kw = q.trim().toLowerCase();
    const list = works.filter((w) => {
      const matchCat = category === "all" || w.category === category;
      const matchKw =
        !kw ||
        w.title.toLowerCase().includes(kw) ||
        w.summary.toLowerCase().includes(kw) ||
        w.author.toLowerCase().includes(kw) ||
        w.categoryName.includes(kw) ||
        w.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
    const sorted = [...list];
    if (sort === "hot") sorted.sort((a, b) => b.views - a.views);
    else if (sort === "likes") sorted.sort((a, b) => b.likes - a.likes);
    else sorted.sort((a, b) => (a.date < b.date ? 1 : -1));
    return sorted;
  }, [q, category, sort]);

  const ranking = useMemo(() => [...works].sort((a, b) => b.likes - a.likes).slice(0, 5), []);

  const pageTitle = activeCategory
    ? `${activeCategory.name}作品 - 爆料大赛参赛作品集`
    : "参赛作品集 - 爆料大赛投稿作品与热门参赛一览";

  usePageSeo({
    title: pageTitle,
    description: activeCategory
      ? `爆料大赛${activeCategory.name}方向的参赛作品与投稿作品合集，共 ${activeCategory.count} 篇，支持关键词搜索与热度排序。`
      : "爆料大赛参赛作品集页面，收录职场爆料、行业内幕、校园见闻、城市现场等方向的投稿作品，支持关键词搜索、分类筛选与热度排序。",
    keywords: `爆料大赛作品集,爆料大赛参赛作品,${activeCategory ? activeCategory.name : "爆料大赛投稿作品"},热门参赛,优秀作品`,
    path: "/works",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: pageTitle,
        url: `${SITE_URL}/works`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
        mainEntity: {
          "@type": "ItemList",
          itemListElement: result.map((w, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/work/${w.id}`,
            name: w.title,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "参赛作品", item: `${SITE_URL}/works` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-background-100 border-b border-background-200 px-4 md:px-8 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "参赛作品" }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">
            {activeCategory ? `${activeCategory.name} · 参赛作品` : "参赛作品集"}
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            {activeCategory
              ? `${activeCategory.desc}。以下是该方向下的投稿作品，可按热度或时间重新排列。`
              : "把每一期通过审核的投稿作品集中展示在这里，覆盖职场、行业、校园、城市、情感与生活六个方向，支持关键词搜索与分类筛选。"}
          </p>
        </section>

        <section className="px-4 md:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28 flex flex-col gap-6">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-layout-grid-line" />
                    </span>
                    作品分类
                  </h2>
                  <ul className="mt-4 flex flex-col gap-1">
                    <li>
                      <button
                        type="button"
                        onClick={() => updateParam("category", "all")}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                          category === "all"
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        <span>全部作品</span>
                        <span className="text-xs opacity-80">{works.length}</span>
                      </button>
                    </li>
                    {categories.map((cat) => (
                      <li key={cat.id}>
                        <button
                          type="button"
                          onClick={() => updateParam("category", cat.id)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                            category === cat.id
                              ? "bg-primary-500 text-background-50"
                              : "text-foreground-700 hover:bg-background-200"
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className={cat.icon} />
                            </span>
                            {cat.name}
                          </span>
                          <span className="text-xs opacity-80">{cat.count.toLocaleString("zh-CN")}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-accent-600">
                      <i className="ri-fire-line" />
                    </span>
                    点赞排行榜
                  </h2>
                  <ol className="mt-4 flex flex-col gap-3">
                    {ranking.map((w, i) => (
                      <li key={w.id}>
                        <Link to={`/work/${w.id}`} className="group flex items-start gap-3 cursor-pointer">
                          <span
                            className={`w-5 h-5 shrink-0 flex items-center justify-center rounded font-heading text-xs font-bold ${
                              i < 3 ? "bg-accent-400 text-primary-950" : "bg-background-200 text-foreground-600"
                            }`}
                          >
                            {i + 1}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {w.title}
                            </span>
                            <span className="mt-1 block text-xs text-foreground-500">
                              {formatNumber(w.likes)} 点赞
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateParam("q", keyword.trim());
                }}
                className="flex flex-col sm:flex-row gap-3"
                role="search"
              >
                <label className="flex-1 flex items-center gap-2.5 bg-background-50 border border-background-300 rounded-md px-4 h-11 focus-within:border-primary-400 transition-colors">
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-search-line" />
                  </span>
                  <input
                    type="search"
                    name="q"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索作品标题、作者或关键词"
                    className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                    aria-label="搜索参赛作品"
                  />
                </label>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-6 h-11 rounded-md whitespace-nowrap transition-colors cursor-pointer"
                >
                  搜索
                </button>
              </form>

              <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-foreground-600">
                  共找到 <strong className="text-foreground-950">{result.length}</strong> 篇作品
                  {q && <span className="text-foreground-500"> · 关键词「{q}」</span>}
                </p>
                <div className="inline-flex items-center gap-1 bg-background-200 rounded-full px-1 py-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => updateParam("sort", opt.key)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        sort === opt.key
                          ? "bg-background-50 text-primary-600"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {result.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {result.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>
              ) : (
                <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                  <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                    <i className="ri-search-eye-line" />
                  </span>
                  <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">暂时没有匹配的作品</h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    换个关键词，或者清除筛选条件，看看其他方向的参赛作品。
                  </p>
                  <button
                    type="button"
                    onClick={() => setParams(new URLSearchParams())}
                    className="mt-5 inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-refresh-line" />
                    </span>
                    清除全部筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}